import React from 'react';
import { Link } from 'react-router-dom';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import { IS_TLA, PATHS } from './utils/platformPaths';

const AlumniSmsTerms = () => {
  const brandName = IS_TLA ? 'TLA Engineer Network' : 'NACME Searchlight';
  const orgName = IS_TLA ? 'The Leadership Alliance' : 'NACME (National Action Council for Minorities in Engineering)';

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            className={`mx-auto w-auto ${IS_TLA ? 'h-48 sm:h-60' : 'h-16 sm:h-20'} mb-4`}
            src={IS_TLA ? tlaLogo : nacmeLogo}
            alt={IS_TLA ? 'TLA Logo' : 'NACME Logo'}
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {brandName} SMS Terms & Conditions
          </h1>
          <p className="text-gray-600">
            Effective Date: February 20, 2026 | Last Updated: February 20, 2026
          </p>
        </div>

        {/* Content */}
        <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
          <div className="prose prose-blue max-w-none">

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Program Description</h2>
            <p className="mb-4">
              {orgName} operates the {brandName} platform. By opting in to our SMS messaging program, you consent to receive text messages from {brandName} on your mobile device. These messages are sent to keep you informed and connected with the {brandName} community.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Types of Messages</h2>
            <p className="mb-2">
              By opting in, you agree to receive the following types of SMS text messages from {brandName}:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li><strong>Account Notifications</strong> &mdash; Account verification, password resets, and security alerts</li>
              <li><strong>Application Updates</strong> &mdash; Status updates on job applications and opportunities you have applied to</li>
              <li><strong>Opportunity Alerts</strong> &mdash; New job postings, internships, or career opportunities that match your profile</li>
              <li><strong>Event Notifications</strong> &mdash; Upcoming networking events, webinars, and professional development opportunities</li>
              <li><strong>Platform Updates</strong> &mdash; Important announcements about the {brandName} platform and your account</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Consent and Opt-In</h2>
            <p className="mb-4">
              By checking the SMS opt-in checkbox during registration and providing your mobile phone number, you expressly consent to receive recurring automated text messages from {brandName} at the phone number you provided. <strong>Consent to receive text messages is not a condition of registration or purchase.</strong> You may register for a {brandName} account without opting in to SMS messages.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Message Frequency</h2>
            <p className="mb-6">
              Message frequency varies based on your account activity and preferences. You may receive up to 10 messages per month, though the actual number may be less depending on activity. Recurring messages will be sent for the duration of your enrollment in the program.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Message and Data Rates</h2>
            <p className="mb-6">
              <strong>Message and data rates may apply.</strong> {brandName} does not charge for SMS messages, but your mobile carrier's standard messaging rates and data charges may apply. Please contact your wireless carrier for details about your messaging plan.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. How to Opt Out</h2>
            <p className="mb-4">
              You can stop receiving text messages at any time by replying <strong>STOP</strong> to any message you receive from us. After you send STOP, you will receive a one-time confirmation message confirming that you have been unsubscribed. After opting out, you will no longer receive SMS messages from {brandName} unless you opt in again.
            </p>
            <p className="mb-6">
              You may also opt out by updating your notification preferences in your account settings, or by contacting us at the information provided below.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Help</h2>
            <p className="mb-6">
              For assistance or more information about our SMS program, reply <strong>HELP</strong> to any message you receive from us, or contact us at <a href="mailto:admin@nacme.app" className="text-blue-600 hover:text-blue-500">admin@nacme.app</a>.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Privacy and Data Protection</h2>
            <p className="mb-4">
              We respect your privacy. Your mobile phone number and opt-in consent information will be used solely for the purpose of sending you the text messages described in this agreement.
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>We will <strong>not sell, rent, or share your mobile phone number</strong> with third parties for their marketing purposes.</li>
              <li>We will <strong>not share your opt-in consent or phone number</strong> with any third party for purposes unrelated to providing our messaging services, except as required by law.</li>
              <li>Your information is handled in accordance with our{' '}
                <Link to={PATHS.userAgreement} className="text-blue-600 hover:text-blue-500 underline">
                  User Agreement
                </Link>.
              </li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Supported Carriers</h2>
            <p className="mb-6">
              Our SMS program is supported on major U.S. wireless carriers including AT&T, Verizon, T-Mobile, Sprint, and most other carriers. Carriers are not liable for delayed or undelivered messages.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to These Terms</h2>
            <p className="mb-6">
              We may update these SMS Terms & Conditions from time to time. If we make material changes, we will notify you via text message or through the {brandName} platform. Continued participation in the SMS program after changes are posted constitutes your acceptance of the updated terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
            <p className="mb-4">
              If you have questions about these SMS Terms & Conditions or need assistance with the SMS program, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="font-medium">{brandName} Support</p>
              <p>Email: <a href="mailto:admin@nacme.app" className="text-blue-600 hover:text-blue-500">admin@nacme.app</a></p>
              <p>Address: 1432 Duke Street, Alexandria, VA 22314</p>
            </div>

            {/* Summary Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Quick Reference</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li><strong>Program:</strong> {brandName} SMS Notifications</li>
                <li><strong>Message Frequency:</strong> Up to 10 messages/month (varies)</li>
                <li><strong>Message & Data Rates:</strong> May apply</li>
                <li><strong>To Opt Out:</strong> Reply STOP to any message</li>
                <li><strong>For Help:</strong> Reply HELP or email admin@nacme.app</li>
                <li><strong>Consent Required:</strong> Not required for account registration</li>
              </ul>
            </div>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-600 italic">
                By opting in to receive SMS messages from {brandName}, you acknowledge that you have read, understood, and agree to be bound by these SMS Terms & Conditions.
              </p>
            </div>
          </div>

          {/* Back to Registration Button */}
          <div className="mt-8 text-center">
            <Link
              to={PATHS.register}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              &larr; Back to Registration
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumniSmsTerms;
